import memoizeOne from 'memoize-one';
import React, { PureComponent } from 'react';
import { VirtualBrand, VirtualPack } from '~/Api/Analytics/VirtualBrand';
import { AnalyticDataSources } from '~/Api/Analytics/VirtualBrand/mapAnalyticVirtualBrand';
import { TimeToPrice } from '~/Api/Analytics/VirtualBrand/TimeToPrice';
import { RegionAvailability } from '~/MedicinalBrandsAnalytics/Region';
import Message from '~/MedicinalBrandsAnalytics/VirtualBrand/Message';
import RegionSelect from '~/MedicinalBrandsAnalytics/VirtualBrand/RegionSelect';
import TimeToPriceChart from '~/MedicinalBrandsAnalytics/VirtualBrand/TimeToPriceChart';
import ActivityIndicator from '~/UI/ActivityIndicator';

import styles from './styles.less';

export interface TabState {
	regionAvailability: RegionAvailability | null;
}

interface Props {
	virtualBrand: VirtualBrand;
	virtualPack: VirtualPack | null;
	virtualBrandAvailability: { [countryCode: string]: AnalyticDataSources };
	virtualPackAvailability: { [virtualPackId: string]: { [countryCode: string]: AnalyticDataSources } };
	isFetching: boolean;
	prices: TimeToPrice[] | null;
	marketingAuthorisationDate: string | null;
	defaultRegionAvailability: RegionAvailability | null;
	state: TabState | null;
	onStateChange(tabState: TabState): void;
	onLoad(brandId: string): void;
}

export default class TimeToPriceTab extends PureComponent<Props> {
	componentDidMount() {
		const { virtualBrand, onLoad } = this.props;
		onLoad(virtualBrand.id);
	}

	selectPrices = memoizeOne((prices: TimeToPrice[], regionAvailability: RegionAvailability): TimeToPrice[] => {
		const filteredPrices = prices.filter((price) => regionAvailability.containsCountry(price.country.alpha2));
		if (!prices.find((price) => !price.hasUnavailableData)) {
			return [];
		}

		filteredPrices.sort((price, otherPrice) => {
			if (price.minDays === null && otherPrice.minDays !== null) {
				return 1;
			}
			if (price.minDays !== null && otherPrice.minDays === null) {
				return -1;
			}
			if (price.minDays !== null && otherPrice.minDays !== null) {
				return price.minDays - otherPrice.minDays;
			}
			if (!price.hasUnavailableData && otherPrice.hasUnavailableData) {
				return 1;
			}
			if (price.hasUnavailableData && !otherPrice.hasUnavailableData) {
				return -1;
			}
			return price.country.alpha2.localeCompare(otherPrice.country.alpha2);
		});
		return filteredPrices;
	});

	render() {
		const {
			virtualBrandAvailability,
			virtualPack,
			isFetching,
			prices,
			marketingAuthorisationDate,
			defaultRegionAvailability,
			state,
			onStateChange,
		} = this.props;

		if (isFetching) {
			return <ActivityIndicator />;
		}
		if (prices === null || !defaultRegionAvailability) {
			return null;
		}

		const regionAvailability = state?.regionAvailability || defaultRegionAvailability;
		const filteredPrices = this.selectPrices(prices, regionAvailability);

		return (
			<div className={styles.content}>
				<div className={styles.leftColumn}>
					<RegionSelect
						allowIds={new Set(['eu-eaa', 'eu-5'])}
						availability={virtualBrandAvailability}
						selectedRegionAvailability={regionAvailability}
						onSelect={(regionAvailability) => onStateChange({ regionAvailability })}
					/>
				</div>
				<div className={styles.rightColumn}>
					{virtualPack && filteredPrices.length > 0 ? (
						<Message>Selected package does not have any effect on the chart.</Message>
					) : null}

					{filteredPrices.length > 0 ? (
						<TimeToPriceChart prices={filteredPrices} />
					) : (
						<Message>{"We're sorry, but the required historic datasets are not available."}</Message>
					)}

					{marketingAuthorisationDate ? (
						<p className={styles.infoText}>
							Marketing authorisation date: <strong>{marketingAuthorisationDate}</strong>
						</p>
					) : null}
				</div>
			</div>
		);
	}
}
